<template>
  <div class="mcontainer">
    <!-- Header -->
    <div class="header-bar">
      <h2>Kelola Group</h2>
      <div class="actions">
        <div class="search">
          <Icon icon="mdi:magnify" width="18" height="18" class="search-icon" />
          <input
            class="search-input"
            v-model="search"
            @input="debouncedFetch"
            placeholder="Search group..."
          />
        </div>
        <select class="form-control" style="margin-bottom: 0;" v-model="status" @change="applyStatus">
          <option value="">Semua</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
        </select>
      </div>
    </div>

    <!-- Card -->
    <div class="card">
      <table class="table">
        <thead>
          <tr>
            <th style="width:120px">Gambar</th>
            <th>Nama Group</th>
            <th>Pembuat</th>
            <th>Kategori</th>
            <th>Member</th>
            <th>Status</th>
            <th style="width:200px">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in list" :key="it.id">
            <td>
              <img v-if="it.images" :src="it.images" class="thumb" alt="">
              <div v-else class="thumb placeholder">
                <Icon icon="mdi:image-off-outline" width="22" height="22" />
              </div>
            </td>
            <td>
              <div class="name">{{ it.title }}</div>
              <div class="desc muted">{{ truncate(it.description, 120) }}</div>
            </td>
            <td>
              {{ (it.owner && it.owner.fullname) || '-' }}
              <span class="muted">({{ (it.owner && it.owner.type) || '-' }})</span>
            </td>
            <td>
              {{ (it.category && it.category.title) || '-' }}
            </td>
            <td>
              {{ it.members_approved_count || 0 }}
            </td>
            <td>
              <span :class="['badge', it.status_text]">{{ it.status_text }}</span>
            </td>
            <td>
              <div class="row-actions">
                <router-link
                  v-if="it.slug"
                  :to="{ name: 'GroupDetail', params: { code: it.slug } }"
                  class="btn btn-light"
                  target="_blank"
                >
                  View
                </router-link>
                <button
                  class="btn btn-success"
                  @click="approve(it.id)"
                  :disabled="loading || it.status_text === 'approved'"
                >
                  Approve
                </button>
                <button
                  class="btn btn-warning"
                  @click="reject(it.id)"
                  :disabled="loading || it.status_text === 'approved'"
                >
                  Reject
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="!loading && list.length === 0">
            <td colspan="7" class="empty">Tidak ada data</td>
          </tr>
        </tbody>
      </table>

      <div class="loading-wrap" v-if="loading"><b-spinner small /></div>

      <!-- Pagination -->
      <div v-if="totalPage > 1" class="pager-wrap">
        <div class="pager-info">Page {{ page }} of {{ totalPage }} ({{ total }} total)</div>
        <div class="pager-controls">
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(1)" title="First">
            <Icon icon="mdi:chevron-double-left" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===1 || loading" @click="go(page-1)" title="Prev">
            <Icon icon="mdi:chevron-left" width="18" height="18" />
          </button>

          <button
            v-for="p in pages"
            :key="p"
            class="num-btn"
            :class="{ active: p === page }"
            :disabled="p === page || loading"
            @click="go(p)"
          >
            {{ p }}
          </button>

          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(page+1)" title="Next">
            <Icon icon="mdi:chevron-right" width="18" height="18" />
          </button>
          <button class="icon-btn" :disabled="page===totalPage || loading" @click="go(totalPage)" title="Last">
            <Icon icon="mdi:chevron-double-right" width="18" height="18" />
          </button>
        </div>
      </div>
      <!-- End Pagination -->
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Icon } from '@iconify/vue2'

export default {
  name: 'GroupAdmin',
  components: { Icon },
  data(){
    return {
      list: [],
      loading: false,
      page: 1,
      totalPage: 1,
      total: 0,
      perPage: 10,
      status: 'pending',
      search: '',
      debouncedFetch: null
    }
  },
  created(){
    this.debouncedFetch = this.debounce(this.fetchList, 500)
    this.fetchList()
  },
  computed:{
    pages(){
      const t=this.totalPage, cur=this.page, arr=[], win=5
      let s=Math.max(1, cur-2), e=Math.min(t, s+win-1)
      if (e-s<win-1) s=Math.max(1, e-win+1)
      for(let i=s;i<=e;i++) arr.push(i)
      return arr
    }
  },
  methods:{
    truncate(s,n){ return s && s.length>n ? s.slice(0,n)+'...' : (s||'') },
    debounce(fn,d){ let t=null; return (...a)=>{clearTimeout(t); t=setTimeout(()=>fn.apply(this,a), d)}},
    async fetchList(){
      this.loading=true
      try{
        const params = new URLSearchParams()
        params.set('page',this.page)
        params.set('per_page',this.perPage)
        if (this.status) params.set('status', this.status)
        if (this.search) params.set('search', this.search)
        const { data } = await axios.get(`group/admin?${params.toString()}`)
        this.list = (data && data.data) ? data.data : []
        this.page = (data && data.current_page) ? data.current_page : 1
        this.totalPage = (data && data.last_page) ? data.last_page : 1
        this.total = (data && data.total) ? data.total : 0
      } finally {
        this.loading=false
      }
    },
    go(p){ if(p<1||p>this.totalPage||p===this.page) return; this.page=p; this.fetchList(); },
    applyStatus(){ this.page=1; this.fetchList(); },
    async approve(id){
      await axios.post(`group/admin/${id}/approve`)
      this.fetchList()
    },
    async reject(id){
      await axios.post(`group/admin/${id}/reject`)
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.header-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.actions{display:flex;gap:10px;align-items:center}
.search{position:relative;min-width:260px;width:320px}
.search-icon{position:absolute;top:10px;left:12px;color:#9ca3af}
.search-input{width:100%;height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 12px 0 36px;background:#fff}
.form-control{height:38px;border:1px solid #e5e7eb;border-radius:10px;padding:0 10px;background:#fff}
.card{background:#fff;border:1px solid #eef2f7;border-radius:14px;padding:16px}
.table{width:100%;border-collapse:separate;border-spacing:0}
.table thead th{background:#fafafa;border-bottom:1px solid #eef2f7;text-align:left;padding:10px;font-weight:600;color:#6b7280}
.table tbody td{border-bottom:1px solid #f3f4f6;padding:10px;vertical-align:top}
.thumb{width:100px;height:70px;object-fit:cover;border-radius:8px;border:1px solid #eee}
.thumb.placeholder{display:flex;align-items:center;justify-content:center;background:#fafafa;color:#9ca3af}
.badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:12px;text-transform:capitalize}
.badge.pending{background:#fff7ed;color:#9a3412;border:1px solid #fed7aa}
.badge.approved{background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0}
.row-actions{display:flex;gap:8px;flex-wrap:wrap}
.btn{height:32px;padding:0 10px;border-radius:8px;border:1px solid #e5e7eb;background:#fff;text-decoration:none;display:inline-flex;align-items:center}
.btn-success{background:#10b981;border-color:#10b981;color:#fff}
.btn-warning{background:#f59e0b;border-color:#f59e0b;color:#fff}
.icon-btn{height:36px;width:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn{min-width:36px;height:36px;border:1px solid #e5e7eb;background:#fff;border-radius:10px}
.num-btn.active{background:#2563eb;border-color:#2563eb;color:#fff}
.pager-wrap{display:flex;align-items:center;justify-content:space-between;margin-top:12px}
.pager-info{font-size:13px;color:#6b7280}
.empty{text-align:center;color:#9ca3af}
.muted{font-size:12px;color:#9ca3af}
</style>
